import Drawer from 'tio-ui/components/drawer';
import Button from 'tio-ui/components/button';
import ConfirmationDialog from 'tio-ui/components/confirmation-dialog';
import AlertBanner from 'tio-ui/components/alert-banner';
import { PhFilePdf } from 'ember-phosphor-icons';
import { Section, VStack } from 'tio-ui/components/layout';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import { trackedFunction } from 'reactiveweb/function';
import Component from '@glimmer/component';
import pageTitle from 'ember-page-title/helpers/page-title';
import { limitsEvaluationDate } from '../../../utils/tuition-assistance/limits-evaluation-date.ts';
import type IntlService from 'ember-intl/services/intl';
import type FeaturesService from '../../../services/features.ts';
import type { TasApplicationExtension as TasApplicationModel } from 'tio-data/data/resources/tas-application.ext';
import type { CustomFieldSignature, CustomFieldOption } from '../../../types/tuition-assistance.ts';
import { trackedObject } from '@ember/reactive/collections';
import { dropTask } from 'ember-concurrency';
import type { Store } from '@warp-drive/core';
import type { TasCourseExtension as TasCourseModel } from 'tio-data/data/resources/tas-course.ext';
import type Inputmask from 'inputmask';
import type SessionContextService from '../../../services/session-context.ts';
import type { NotificationsService } from 'tio-ui/components/notifications';
import type TuitionAssistanceService from '../../../services/tuition-assistance.ts';
import type Owner from '@ember/owner';
import {
  getSingleValueForTasField,
  setSingleValueForTasField,
  setTrackedTasFieldValue,
  mergeCustomFieldValues,
  getSortedOptionsByFieldName,
} from '../../../utils/tuition-assistance/fields.ts';
import {
  approvedTotalFieldForState,
  canSyncApprovedTotalFromRequest,
} from '../../../utils/tuition-assistance/approved-total-sync.ts';
import {
  reloadRecord,
  saveRecord,
  saveExistingRecordWithChangelog,
} from 'tio-data/requests/save-record';
import { periodsOfEligibilityRequest } from 'tio-data/requests/tas-participant-actions';
import {
  createPdfAssetRequest,
  updateApprovedTotalRequest,
} from 'tio-data/requests/tas-application-actions';
import type { PeriodOfEligibility } from 'tio-data/types/tuition-assistance';
import type { EligibilityStatus } from 'tio-data/data/resources/eligibility-status.type';
import * as v from 'valibot';
import type { ValidationErrorSignature } from '../../tuition-assistance/forms/courses/grade-select-options.ts';
import type { LimitsStatusDataSignature } from '../../../services/tuition-assistance.ts';
import type RouterService from '@ember/routing/router-service';

type AiRecommendationResponse = {
  recommendation_summary?: {
    status?: string;
    calculation_details?: unknown;
  };
};

// Used throughout the component to type-cast programTemplate.fields
type ProgramTemplateFields =
  Record<string, { visible?: boolean; label?: string; required?: boolean }> | undefined;

import ApprovalFooter from './approval-footer.gts';
import AiAnalysisDrawer from './drawers/ai-analysis.gts';
import CommentsDrawer from './drawers/comments.gts';
import CoursesDrawer from './drawers/courses.gts';
import PartnerInfoDrawer from './drawers/partner-info.gts';
import FinancialDetailsDrawer from './drawers/financial-details.gts';
import AttachmentsDrawer from './drawers/attachments.gts';
import AttachmentsAnalysisView from './drawers/ai-analysis/attachments-analysis-view.gts';
import TimelineDrawer from './drawers/timeline.gts';
import ApprovalQueueStatusDrawer from './drawers/approval-queue-status.gts';
import FinancialDetailsAdminSummary from './financial-details-admin-summary.gts';
import ApprovalDecisionBanner from './approval-decision-banner.gts';
import AddScholarshipsDrawer from '../add-scholarships-drawer.gts';
import RecentCommentPreview from './recent-comment-preview.gts';
import ApplicationDetailsSection from './application-details-section.gts';
import ExpensesSection from './expenses-section.gts';
import RepaymentWarningBanner from './repayment-warning-banner.gts';

const classificationOptions = [
  { label: 'Final Approval Document', key: 'COMPLETION_DOCUMENTS' },
  { label: 'Approval Document', key: 'COURSE_APPROVAL_DOCUMENTS' },
  { label: 'Misc Approval Document (Admin)', key: 'APPLICATION_LIFECYCLE_DOCUMENTS' },
];

interface ApprovalPageSignature {
  Element: HTMLElement;
  Args: {
    model: TasApplicationModel;
    /** Query param to enable AI analysis in production (tio-admin only) */
    ai?: boolean;
    /** Admin-only read-only mode for applications outside the current user's approval queue. */
    isReadonlyView?: boolean;
    /** Optional callback to open the CourseCash drawer (admin-only) */
    onCourseCashOpen?: () => void;
  };
  Blocks: {
    header: [];
    alerts?: [];
  };
}

// DOM target of an inputmask-bound element. `userOptions` exists on the
// runtime Instance but is not part of @types/inputmask, so we narrow it here.
type InputmaskTarget = HTMLInputElement & {
  inputmask?: Inputmask.Instance & { userOptions?: { alias?: string } };
};

export default class TasApprovalPage extends Component<ApprovalPageSignature> {
  @service declare sessionContext: SessionContextService;
  @service declare intl: IntlService;
  @service declare store: Store;
  @service declare tuitionAssistance: TuitionAssistanceService;
  @service declare features: FeaturesService;
  @service declare notifications: NotificationsService;
  @service declare router: RouterService;

  @tracked selectedDrawerType: string | null = null;
  @tracked isDrawerOpen = false;
  @tracked selectedCourseIndex = 0;
  @tracked showUploadSection = false;
  @tracked showGeneratePdfConfirm = false;
  @tracked selectedClassification = '';
  @tracked uploadClassificationField = '';

  // Scholarship drawer state (opened from ExpensesSection, rendered at page level)
  @tracked isScholarshipDrawerOpen = false;

  // Inline course expense editing
  @tracked editingCourseExpensesIndex: number | null = null;
  @tracked courseExpenseFieldsCopy: TasCourseModel['fields'] | null = null;
  @tracked expenseChangelog = '';
  /** Requested total as of entering expense edit mode — see canSyncApprovedTotalFromRequest */
  @tracked requestedTotalBeforeExpenseEdit: number | null = null;

  // Inline course detail editing
  @tracked editingCourseDetailsIndex: number | null = null;
  @tracked courseDetailFieldsCopy: TasCourseModel['fields'] | null = null;
  @tracked courseCustomFieldsCopy: CustomFieldSignature[] | null = null;
  @tracked detailChangelog = '';

  // Inline application details editing — most state lives in ApplicationDetailsSection.
  // These two stay here because other parts of this page depend on them:
  // isEditingApplicationDetails is read by ApprovalFooter, and applicationFieldsCopy
  // is written by syncScholarshipsFromApplication (scholarship drawer).
  @tracked isEditingApplicationDetails = false;
  @tracked applicationFieldsCopy: TasApplicationModel['fields'] | null = null;

  // Financial details state
  @tracked editableApplicationTotal = 0;
  @tracked editableCourseCashTotal = 0;

  // Comments drawer tab state
  @tracked commentsInitialTab: 'approver' | 'employee' = 'approver';

  // V2 Limits data
  @tracked private fetchedLimitsData: LimitsStatusDataSignature | undefined = undefined;
  // Kept separate from the getter so a failed refresh withholds the payload instead of
  // serving a balance the latest fetch could not confirm.
  @tracked private limitsLoadFailed = false;

  // Eligibility status data (admin decision-assist banner)
  @tracked eligibilityData: EligibilityStatus | undefined = undefined;
  @tracked eligibilityLoading = false;

  // Employee eligibility periods - used for checking if employee is eligible today
  periodsOfEligibilityData = trackedFunction(this, async () => {
    try {
      const programInstance = this.args.model.tasProgramInstance;
      if (!programInstance?.employee?.tasParticipant?.id) {
        return [];
      }

      const response = await this.store.request(
        periodsOfEligibilityRequest(programInstance.employee.tasParticipant.id)
      );
      // Legacy adapter filtered the per-program-code value off the raw response.
      const periods = response.content as PeriodOfEligibility;
      return periods[programInstance.tasProgramTemplate.code] as [string, string][];
    } catch (e) {
      console.warn('Error fetching eligibility periods:', e);
      return [];
    }
  });

  constructor(owner: Owner, args: ApprovalPageSignature['Args']) {
    super(owner, args);

    // Initialize financial state
    const calculateTotalByClaimsFinancials =
      args.model.tasProgramInstance?.tasProgramTemplate?.calculateTotalByClaimsFinancials;

    this.editableApplicationTotal = calculateTotalByClaimsFinancials
      ? args.model.recalculateClaimsFinancialsAfterAdjacentUpdate().CF_REIMBURSEMENT_TOTAL
      : ((getSingleValueForTasField(
          args.model.fieldForEditableApprovedTotal,
          args.model.fields
        ) as number) ??
        (args.model.approvedTotal as number) ??
        (args.model.requestedTotal as number));

    this.editableCourseCashTotal = Number(args.model.cardPreApprovedTotal);

    void this.loadLimitsData();

    if (this.features.isAdminApp) {
      void this.loadEligibilityData();
    }
  }

  /**
   * Loads V2 limits data for the employee and application
   * This provides scheme-level limits information used to display remaining balance
   */
  loadLimitsData = async () => {
    const employee = this.args.model.tasProgramInstance?.employee;
    if (!employee) {
      console.warn('Cannot load limits data: employee not found on application');
      return;
    }

    try {
      this.fetchedLimitsData = await this.tuitionAssistance.getV2LimitsStatus(
        employee,
        this.args.model,
        limitsEvaluationDate(this.args.model)
      );
      this.limitsLoadFailed = false;
    } catch (error) {
      this.limitsLoadFailed = true;
      console.error('Error loading V2 limits data:', {
        applicationId: this.args.model.id,
        employeeId: employee.id,
        error,
      });
    }
  };

  get limitsData(): LimitsStatusDataSignature | undefined {
    return this.limitsLoadFailed ? undefined : this.fetchedLimitsData;
  }

  // True when the read failed, as against succeeding with no limit configured; both
  // leave limitsData absent, so only this separates them for display.
  get isLimitsUnavailable() {
    return this.limitsLoadFailed;
  }

  /**
   * Loads eligibility status data for the admin decision-assist banner.
   * This provides participant state, employment status, and computed eligibility.
   */
  loadEligibilityData = async () => {
    const applicationId = this.args.model.id;
    if (!applicationId) {
      return;
    }

    this.eligibilityLoading = true;
    try {
      this.eligibilityData = await this.tuitionAssistance.getEligibilityStatus(applicationId);
    } catch (error) {
      console.error('Error loading eligibility status:', {
        applicationId,
        error,
      });
    } finally {
      this.eligibilityLoading = false;
    }
  };

  openDrawer = (type: string) => {
    this.selectedDrawerType = type;
    this.isDrawerOpen = true;
  };

  openCommentsDrawer = (tab: 'approver' | 'employee' = 'approver') => {
    this.commentsInitialTab = tab;
    this.openDrawer('comments');
  };

  closeDrawer = () => {
    this.isDrawerOpen = false;
    this.selectedDrawerType = null;

    // Cancel any active editing when closing drawer
    if (this.editingCourseExpensesIndex !== null) {
      this.editingCourseExpensesIndex = null;
      this.courseExpenseFieldsCopy = null;
      this.expenseChangelog = '';
    }

    if (this.editingCourseDetailsIndex !== null) {
      this.editingCourseDetailsIndex = null;
      this.courseDetailFieldsCopy = null;
      this.detailChangelog = '';
    }
  };

  courseTabDidChange = (index: number) => {
    this.selectedCourseIndex = index;
  };

  openCourseDrawer = (courseIndex: number) => {
    this.selectedCourseIndex = courseIndex;
    this.openDrawer('course-details');
  };

  toggleUploadSection = () => {
    this.showUploadSection = !this.showUploadSection;
    // Reset classification when opening
    if (this.showUploadSection) {
      this.selectedClassification = '';
      this.uploadClassificationField = '';
    }
  };

  hideUploadSection = () => {
    this.showUploadSection = false;
    this.selectedClassification = '';
    this.uploadClassificationField = '';
  };

  get canGeneratePdfAsset() {
    // Mirrors the backend guards: TasPdfAssetService only renders voucher
    // documents for VOUCHER_PROGRAM templates (plus a company allowlist).
    return (
      this.consumingApp === 'tio-admin' &&
      !!this.sessionContext.user.isTioAdmin &&
      !!this.args.model.tasProgramInstance?.tasProgramTemplate?.isVoucherProgram
    );
  }

  // The backend renders the voucher template named by VOUCHER_FILENAME and
  // names the generated file after it, so the dialog can say which document.
  get voucherDocumentName() {
    return (
      this.args.model.tasProgramInstance?.tasProgramTemplate?.fields?.['VOUCHER_FILENAME']
        ?.values?.[0] ?? ''
    );
  }

  // Generated program PDFs are stored with this classification, so existing
  // ones signal the admin may be about to create a duplicate.
  get existingLifecycleDocumentsCount() {
    return this.allAttachments.filter(
      (asset) =>
        asset.classification === 'APPLICATION_LIFECYCLE_DOCUMENTS' &&
        asset.attachmentDeletedAt === null
    ).length;
  }

  openGeneratePdfConfirm = () => {
    this.showGeneratePdfConfirm = true;
  };

  closeGeneratePdfConfirm = () => {
    this.showGeneratePdfConfirm = false;
  };

  // Support backdoor: the server only enqueues PDF jobs for allowlisted
  // companies (CreatePdfAssetPolicy) and responds 400 otherwise.
  generatePdfAsset = dropTask(async () => {
    // Records loaded from the API always carry an id, but the schema types it
    // as nullable — bail with the failure toast rather than emit a bad URL.
    const applicationId = this.args.model.id;
    if (!applicationId) {
      this.notifications.add(
        this.intl.t('common.tas.approval.drawer.attachments.generate_pdf_failed'),
        { appearance: 'error' }
      );
      this.showGeneratePdfConfirm = false;
      return;
    }

    try {
      await this.store.request(createPdfAssetRequest(applicationId));
      this.notifications.add(
        this.intl.t('common.tas.approval.drawer.attachments.generate_pdf_started'),
        { appearance: 'success' }
      );
    } catch {
      this.notifications.add(
        this.intl.t('common.tas.approval.drawer.attachments.generate_pdf_failed'),
        { appearance: 'error' }
      );
    } finally {
      this.showGeneratePdfConfirm = false;
    }
  });

  attachmentClassificationDidChange = (classification: string) => {
    this.selectedClassification = classification;
    this.uploadClassificationField = classification || 'APPLICATION_LIFECYCLE_DOCUMENTS';
  };

  // Scholarship drawer handlers (the drawer itself renders at page level because
  // closeScholarshipDrawer syncs saved scholarships back into applicationFieldsCopy)
  openScholarshipDrawer = () => {
    if (this.isReadonlyView) return;

    this.isScholarshipDrawerOpen = true;
  };

  closeScholarshipDrawer = () => {
    this.isScholarshipDrawerOpen = false;
    // Sync scholarships from the saved application back to the tracked copy
    this.syncScholarshipsFromApplication();
  };

  syncScholarshipsFromApplication() {
    // If we're in edit mode and have a tracked copy, update it with the latest scholarship data
    if (this.applicationFieldsCopy && this.args.model.fields['SCHOLARSHIPS_RECEIVED']) {
      this.applicationFieldsCopy['SCHOLARSHIPS_RECEIVED'] =
        this.args.model.fields['SCHOLARSHIPS_RECEIVED'];
    }
  }

  toggleCourseExpenseEdit = () => {
    const isCurrentlyEditing = this.editingCourseExpensesIndex === this.selectedCourseIndex;

    if (isCurrentlyEditing) {
      // Cancel edit mode - discard changes
      this.editingCourseExpensesIndex = null;
      this.courseExpenseFieldsCopy = null;
      this.expenseChangelog = '';
      this.requestedTotalBeforeExpenseEdit = null;
    } else {
      // Enter edit mode - create tracked copy
      const course = this.selectedCourse;
      if (!course) return;

      this.courseExpenseFieldsCopy = trackedObject(course.fields);
      this.expenseChangelog = '';
      // Captured before the edit moves it — saveCourseExpenses compares it against the
      // approved total to tell "still tracking the request" from "somebody set this".
      this.requestedTotalBeforeExpenseEdit = this.requestedTotalForApproval;
      this.editingCourseExpensesIndex = this.selectedCourseIndex;
    }
  };

  updateCourseExpenseField = (fieldName: string, event: Event) => {
    if (!this.courseExpenseFieldsCopy) return;

    let valueToSet: number;
    const target = event.target as InputmaskTarget;

    if (target?.inputmask?.userOptions?.alias === 'currency') {
      // Get unmasked value from inputmask and convert to cents
      valueToSet = Number(target.inputmask?.unmaskedvalue()) * 100;
    } else {
      // Fallback: parse string value and convert to cents
      const value = target?.value ?? '';
      valueToSet = value ? parseFloat(value.replace(/[^0-9.-]/g, '')) * 100 : 0;
    }

    setSingleValueForTasField(
      fieldName as keyof TasCourseModel['fields'],
      valueToSet,
      this.courseExpenseFieldsCopy
    );
  };

  updateExpenseChangelog = (value: string) => {
    this.expenseChangelog = value;
  };

  saveCourseExpenses = dropTask(async () => {
    const course = this.selectedCourse;
    if (!course || !this.courseExpenseFieldsCopy) {
      return;
    }

    // Validate changelog before saving
    if (!this.isExpenseFormValid) {
      return;
    }

    try {
      course.fields = this.courseExpenseFieldsCopy;

      // WarpDrive mutation (Phase 2.7) — replaces course.save({ adapterOptions: { changelog } }).
      await saveExistingRecordWithChangelog(this.store, course, this.expenseChangelog);

      const application = this.args.model;

      // Handle direct pay program totals recalculation if needed
      if (
        course.tasApplication?.tasProgramInstance?.tasProgramTemplate?.isDirectPayProgram &&
        course.tasApplication.paymentMethod === 'TAS.AssistanceManner.COURSE_CASH'
      ) {
        this.setSubtotalsForDirectPay();
        // Application was modified by setSubtotalsForDirectPay, save it
        // WarpDrive mutation (Phase 2.7) — replaces application.save({ adapterOptions: { changelog } }).
        await saveExistingRecordWithChangelog(this.store, application, this.expenseChangelog);
      }

      // Recalculate claims financials if needed
      if (application.tasProgramInstance?.tasProgramTemplate?.calculateTotalByClaimsFinancials) {
        const calculatorResults = application.recalculateClaimsFinancialsAfterAdjacentUpdate();

        if (application.approvedTotal !== calculatorResults.CF_REIMBURSEMENT_TOTAL) {
          await this.updateApprovedTotal(calculatorResults.CF_REIMBURSEMENT_TOTAL);
        }
      }

      // Plain reimbursement programs: keep the approved total tracking the request the
      // way it does at submission, so an approver who corrects tuition/fees here sees the
      // corrected amount in the approve modal instead of retyping it (SUP-7638).
      // Kept out of the enclosing catch: the expenses themselves are already saved, so a
      // failure here must not strand the drawer in edit mode as if nothing had happened.
      // The approve modal still shows the current expenses, so the approver can proceed.
      try {
        await this.#syncApprovedTotalWithCourseExpenses();
      } catch (error) {
        console.error('Error syncing approved total with updated course expenses:', error);
        this.notifications.add(this.intl.t('common.tas.approval.approved_total_sync_failed'), {
          appearance: 'error',
        });
      }

      this.tuitionAssistance.refreshActiveLimitsSummaryComponents();

      // Exit edit mode and clear state
      this.editingCourseExpensesIndex = null;
      this.courseExpenseFieldsCopy = null;
      this.expenseChangelog = '';
      this.requestedTotalBeforeExpenseEdit = null;

      this.courseExpensesFor(course);
    } catch (error) {
      console.error('Error saving course expenses:', error);
      // TODO: Show error notification to user
    }
  });

  /**
   * Carry an approver's course-expense edit through to the approved total, but only
   * while that total is still tracking the request rather than holding somebody's
   * deliberate amount (see canSyncApprovedTotalFromRequest).
   *
   * The ownership check reads server state, so the application is refreshed first: an
   * amount another approver recorded since this page loaded would otherwise be invisible
   * here and get overwritten. That narrows the window to this round-trip rather than
   * closing it — a concurrent decision landing mid-flight still needs a conditional
   * update on the API to be fully safe.
   *
   * The reload carries `tas-assistances` because the guard's ledger check reads them;
   * without the include it would run over an empty array and fail open.
   */
  #syncApprovedTotalWithCourseExpenses = async () => {
    const application = this.args.model;
    const requestedTotalBeforeEdit = this.requestedTotalBeforeExpenseEdit;

    if (!canSyncApprovedTotalFromRequest(application, requestedTotalBeforeEdit)) {
      return;
    }

    await reloadRecord(this.store, application, { include: 'tas-assistances' });

    if (!canSyncApprovedTotalFromRequest(application, requestedTotalBeforeEdit)) {
      return;
    }

    const requestedTotal = this.requestedTotalForApproval;

    if (application.approvedTotal === requestedTotal) {
      return;
    }

    await this.updateApprovedTotal(
      requestedTotal,
      this.expenseChangelog || 'Course expenses updated'
    );
  };

  /**
   * The requested total as an approved amount. `requestedTotal` already applies the
   * program's reimbursement percentage; GPA-based programs additionally cap it at the
   * tier the recorded GPA earns, the same way the submit flow does.
   */
  get requestedTotalForApproval() {
    const application = this.args.model;
    const requestedTotal = application.requestedTotal;

    if (!application.requiresGPA) {
      return requestedTotal;
    }

    const gpa = Number(getSingleValueForTasField('GRADE_POINT_AVERAGE', application.fields) || 0);

    return Math.min(this.tuitionAssistance.setTotalBasedOnGPA(gpa), requestedTotal);
  }

  updateApprovedTotal = async (
    approvedTotal: number,
    reason: string = 'Course expenses updated',
    additionalFieldsToUpdate?: Record<string, unknown>
  ) => {
    const additionalFieldValues: Record<string, unknown> = {
      ...additionalFieldsToUpdate,
    };

    const calculateTotalByClaimsFinancials =
      this.args.model.tasProgramInstance?.tasProgramTemplate?.calculateTotalByClaimsFinancials;

    if (calculateTotalByClaimsFinancials) {
      const claims = this.args.model.recalculateClaimsFinancialsAfterAdjacentUpdate();
      additionalFieldValues['CLAIMS_FINANCIALS'] = claims;
    }

    // Don't save the course cash total separately - let updateApprovedTotal handle it
    // This ensures the approved_total_log captures the correct "from" value

    // WarpDrive mutation builder (Phase 2.7) — replaces
    // store.adapterFor('tas-application').updateApprovedTotal(model, total, reason, additionalFieldValues)
    // (PATCH /update-approved-total + reload({ include: 'tas-assistances' })).
    await this.store.request(
      updateApprovedTotalRequest(
        this.args.model.id as string,
        approvedTotal,
        reason,
        additionalFieldValues
      )
    );
    await reloadRecord(this.store, this.args.model, { include: 'tas-assistances' });
    const updatedApplication = this.args.model;

    const fieldToCheck = approvedTotalFieldForState(this.args.model.state);
    const actualUpdatedValue = getSingleValueForTasField(
      fieldToCheck,
      updatedApplication.fields
    ) as number;

    if (actualUpdatedValue !== approvedTotal) {
      throw new Error(
        `Unable to update approved total. expected=${approvedTotal} actual=${actualUpdatedValue} (checked field: ${fieldToCheck})`
      );
    }

    this.editableApplicationTotal = updatedApplication.approvedTotal as number;
    // `courseCashTotal` is not a typed model property; the legacy adapter return
    // was `any`, so this read resolved to `undefined` at runtime. Preserve that
    // exact behavior through the now-strict model type.
    this.editableCourseCashTotal = (updatedApplication as { courseCashTotal?: number })
      .courseCashTotal as number;
    this.tuitionAssistance.refreshActiveLimitsSummaryComponents();
  };

  // Financial details methods
  setApplicationTotal = (value: number) => {
    this.editableApplicationTotal = Math.round(value * 100);
  };

  setCourseCashTotal = (value: number) => {
    this.editableCourseCashTotal = Math.round(value * 100);
  };

  // (state shared with the extracted section — see the tracked fields above)
  setIsEditingApplicationDetails = (value: boolean) => {
    this.isEditingApplicationDetails = value;
  };

  setApplicationFieldsCopy = (fields: TasApplicationModel['fields'] | null) => {
    this.applicationFieldsCopy = fields;
  };

  // Direct assignment of the editable approved total (raw cents).
  // NOTE: intentionally not setApplicationTotal, which converts dollars to cents.
  setEditableApplicationTotal = (value: number) => {
    this.editableApplicationTotal = value;
  };

  setCourseCashApprovedTotal = async (amount: number) => {
    const paymentMethodIsCourseCash =
      this.args.model.paymentMethod === 'TAS.AssistanceManner.COURSE_CASH';
    if (!paymentMethodIsCourseCash && !amount) return;

    setTrackedTasFieldValue(this.args.model, 'CARD_FINAL_APPROVED_TOTAL', amount);
    // WarpDrive mutation (Phase 2.7) — replaces this.args.model.save().
    await saveRecord(this.store, this.args.model);
  };

  didUpdateClaimsFinancials = (claimsFinancials: { CF_REIMBURSEMENT_TOTAL: number }) => {
    // setTrackedTasFieldValue reassigns `fields` so WarpDrive serializes the
    // change; an in-place write to the live record is dropped from the PATCH.
    setTrackedTasFieldValue(this.args.model, 'CLAIMS_FINANCIALS', claimsFinancials);
    this.editableApplicationTotal = claimsFinancials.CF_REIMBURSEMENT_TOTAL;
  };

  saveUpdatedClaimsFinancialsWithApprovedTotal = async (
    claimsFinancials: { CF_REIMBURSEMENT_TOTAL: number },
    reason: string
  ) => {
    this.editableApplicationTotal = claimsFinancials.CF_REIMBURSEMENT_TOTAL;
    this.didUpdateClaimsFinancials(claimsFinancials);
    await this.updateApprovedTotal(this.editableApplicationTotal, reason);
  };

  updateInvoiceDate = (date: string) => {
    setTrackedTasFieldValue(this.args.model, 'INVOICE_DATE', date);
  };

  setSubtotalsForDirectPay() {
    const { requestedCourseCashSubtotalLessScholarships, requestedNonCourseCashSubtotal } =
      this.args.model;

    const typeClassification = this.args.model.typeClassification || '';
    const isTypeTwoProgram = typeClassification === 'TAS.ProgramType.2';

    const courseCashTotalField = isTypeTwoProgram
      ? 'CARD_FINAL_APPROVED_TOTAL'
      : 'CARD_PRE_APPROVED_TOTAL';
    const nonCourseCashTotalField = isTypeTwoProgram
      ? 'FINAL_APPROVED_TOTAL'
      : 'PRE_APPROVED_TOTAL';

    setTrackedTasFieldValue(
      this.args.model,
      courseCashTotalField,
      requestedCourseCashSubtotalLessScholarships
    );
    setTrackedTasFieldValue(
      this.args.model,
      nonCourseCashTotalField,
      requestedNonCourseCashSubtotal
    );
  }

  get isAiAnalysis() {
    return this.selectedDrawerType === 'ai-analysis';
  }

  get isComments() {
    return this.selectedDrawerType === 'comments';
  }

  get isEmployerInfo() {
    return this.selectedDrawerType === 'employer-info';
  }

  get isFinancialDetails() {
    return this.selectedDrawerType === 'financial-details';
  }

  get isAttachments() {
    return this.selectedDrawerType === 'attachments';
  }

  get isTimeline() {
    return this.selectedDrawerType === 'timeline';
  }

  get isCourseDetails() {
    return this.selectedDrawerType === 'course-details';
  }

  get isReassignApprover() {
    return this.selectedDrawerType === 'reassign-approver';
  }

  get consumingApp(): string {
    return this.features.isAdminApp
      ? 'tio-admin'
      : this.features.isEmployeeApp
        ? 'tio-employee'
        : this.features.isAgentApp
          ? 'tio-agent'
          : 'unknown';
  }

  /**
   * Get the correct route for program template based on the consuming app
   * tio-admin: authenticated.tuition-assistance.program-builder.program-template
   * tio-employee: authenticated.admin.tuition-assistance.program-templates.show
   */
  get programTemplateRoute(): string | undefined {
    const programTemplate = this.args.model.tasProgramInstance?.tasProgramTemplate;
    if (!programTemplate?.id) return undefined;

    return this.consumingApp === 'tio-admin'
      ? 'authenticated.tuition-assistance.program-builder.program-template'
      : 'authenticated.admin.tuition-assistance.program-templates.show';
  }

  get selectedCourse() {
    const courses = this.args.model.activeCourses || [];
    return courses[this.selectedCourseIndex];
  }

  get currentDrawerComponent() {
    return null; // We'll use conditional rendering instead
  }

  get approverInstructionsItems() {
    const instructions =
      this.args.model.tasProgramInstance?.tasProgramTemplate?.programApproverInstructions || [];
    return instructions.map((instruction) => ({
      heading: instruction.heading || `Instruction ${instruction.ordinal}`,
      body: instruction.body,
      ordinal: instruction.ordinal,
    }));
  }

  get employeeFaqsItems() {
    const faqs = this.args.model.tasProgramInstance?.tasProgramTemplate?.programFaqs || [];
    return faqs.map((faq) => ({
      heading: faq.heading || faq.question || 'FAQ',
      body: faq.body || faq.answer || '',
      ordinal: faq.ordinal || 0,
    }));
  }

  get approvalStatus() {
    // Map application state to human-readable status
    const state = this.args.model.state;
    if (state?.includes('PENDING_COURSES_APPROVAL')) {
      return 'Pending Courses Approval';
    } else if (state?.includes('PENDING_EVIDENCE_APPROVAL')) {
      return 'Pending Evidence Approval';
    } else if (state?.includes('APPROVED')) {
      return 'Approved';
    } else if (state?.includes('REJECTED')) {
      return 'Rejected';
    }
    return 'Pending Review';
  }

  get isPostPaymentReview() {
    return (
      this.args.model.state === 'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL' &&
      (this.args.model.amounts?.paid ?? 0) > 0
    );
  }

  get applicationNumber() {
    return this.args.model.id || 'Unknown';
  }

  get employeeName() {
    const employee = this.args.model.tasProgramInstance?.employee;
    const person = employee?.person;
    if (person?.firstName && person?.lastName) {
      return `${person.firstName} ${person.lastName}`;
    }
    return person?.fullName || 'Unknown';
  }

  get payrollId() {
    return this.args.model.tasProgramInstance?.employee?.payrollId || 'Unknown';
  }

  get remainingBalance() {
    // Use V2 limits data - scheme.available is the correct limit to display
    const schemeAvailable = this.limitsData?.details?.scheme?.available;

    if (schemeAvailable !== undefined && schemeAvailable !== null) {
      return `$${(schemeAvailable / 100).toFixed(2)}`;
    }

    // Fallback to courseCashAvailableBalance if V2 data not yet loaded
    const balance = this.args.model.courseCashAvailableBalance;
    return balance !== undefined ? `$${(balance / 100).toFixed(2)}` : '$0.00';
  }

  get isParticipantInactive() {
    return !this.args.model.tasProgramInstance?.employee?.tasParticipant?.isActive;
  }

  get annualLimit() {
    // Use V2 limits data - scheme.annual_limit is the correct annual limit
    const annualLimit = this.limitsData?.details?.scheme?.annual_limit;

    if (annualLimit !== undefined && annualLimit !== null) {
      return `$${(annualLimit / 100).toFixed(2)}`;
    }

    return 'N/A';
  }

  get courseCount() {
    return this.args.model.activeCourses?.length || 0;
  }

  get useBalanceMultiplier() {
    return (
      this.args.model.tasProgramInstance?.tasProgramTemplate
        ?.useBalanceMultiplierForProgramLimits || false
    );
  }

  get allAttachments() {
    // Assets can be attached to the program instance, course, application, or assistance
    const programInstanceAssets = this.args.model.tasProgramInstance?.tasAssets || [];
    const courseAssets =
      this.args.model.activeCourses?.flatMap((course) => course.tasAssets || []) || [];
    const appAssets = this.args.model.tasAssets || [];
    const assistanceAssets =
      this.args.model.tasAssistances?.flatMap((assistance) => assistance.tasAssets || []) || [];

    // Deduplicate by ID since assets may be linked to multiple relationships
    const seenIds = new Set<string>();
    const allAssets = [
      ...programInstanceAssets,
      ...courseAssets,
      ...appAssets,
      ...assistanceAssets,
    ];
    return allAssets.filter((asset) => {
      const id = asset.id;
      if (!id || seenIds.has(id)) return false;
      seenIds.add(id);
      return true;
    });
  }

  get allAssetSummaries() {
    const summaries = this.allAttachments
      .map((asset) => asset.assetSummary)
      .filter((summary): summary is NonNullable<typeof summary> => summary != null);

    return summaries;
  }

  get timelineHistory() {
    return this.args.model.fullHistory || [];
  }

  get hasTimelineData() {
    return this.timelineHistory.length > 0;
  }

  get aiRecommendation() {
    const recommendations = this.args.model.aiRecommendations;
    if (!recommendations || recommendations.length === 0) return undefined;
    const sorted = [...recommendations].sort(
      (a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()
    );
    return sorted[0];
  }

  get aiRecommendationStatus() {
    const responseJson = this.aiRecommendation?.responseJson as
      AiRecommendationResponse | undefined;
    const status = responseJson?.recommendation_summary?.status;
    if (!status) return null;

    const statusMap: Record<string, string> = {
      approved_for_requested_amount: 'Approve Application',
      approved_for_less_than_requested: 'Approve (Less Than Requested)',
      approved_for_more_than_requested: 'Approve (More Than Requested)',
      denied: 'Do Not Approve',
    };

    return statusMap[status] || status;
  }

  get aiCalculationDetails() {
    const responseJson = this.aiRecommendation?.responseJson as
      AiRecommendationResponse | undefined;
    const details = responseJson?.recommendation_summary?.calculation_details;
    // If details is an object, stringify it for display, otherwise return as string
    if (details && typeof details === 'object') {
      return JSON.stringify(details, null, 2);
    }
    return details ? String(details) : null;
  }

  get hasAiRecommendation() {
    return !!this.aiRecommendation && !!this.aiRecommendationStatus;
  }

  /**
   * Merge updated custom fields into existing fields array
   * Matches fields by label and province, then replaces with updated values
   * Pattern adopted from type-two-or-three.gts for consistency
   *
   * @param existingFields - The current array of custom fields
   * @param updatedFields - The array of fields with updated values
   * @returns Merged array with updates applied
   */
  updateFieldsWithNewValues = (
    existingFields: CustomFieldSignature[],
    updatedFields: CustomFieldSignature[]
  ): CustomFieldSignature[] => {
    // Pure merge that returns a NEW array (never mutates `existingFields`). The
    // approver review flow only edits existing custom fields, so unmatched
    // updates are NOT appended (appendNew defaults to false).
    return mergeCustomFieldValues(existingFields, updatedFields);
  };

  /**
   * Check if user can edit course expenses
   */
  get canEditCourse() {
    return !this.isReadonlyView && (this.sessionContext.canEditTasApplication ?? false);
  }

  get isReadonlyView() {
    return this.args.isReadonlyView === true;
  }

  /**
   * Course Cash applications with reserved funds should not allow editing course tuition,
   * because the tuition amount is tied to the reserved card amount and editing it
   * creates a discrepancy (DEV-6498).
   */
  get isCourseCashTuitionLocked() {
    const isCourseCash = this.args.model.paymentMethod === 'TAS.AssistanceManner.COURSE_CASH';
    const hasReservedFunds = (this.args.model.amounts?.course_cash?.reserved ?? 0) > 0;
    return isCourseCash && hasReservedFunds;
  }

  /**
   * Check if the currently selected course is being edited
   */
  get isEditingCourseExpenses() {
    return this.editingCourseExpensesIndex === this.selectedCourseIndex;
  }

  /**
   * Get list of editable expense field names
   */
  get editableExpenseFields() {
    return ['COURSE_TUITION', 'COURSE_BOOKS', 'COURSE_LABS', 'COURSE_SOFTWARE', 'COURSE_FEES'];
  }

  /**
   * Get course expense value for editing
   * Converts cents to dollars for display in currency input
   */
  getCourseExpenseValue = (fieldName: string): string => {
    if (!this.courseExpenseFieldsCopy) return '0';
    const value = getSingleValueForTasField(
      fieldName as keyof TasCourseModel['fields'],
      this.courseExpenseFieldsCopy
    );
    // Convert cents to dollars for display (divide by 100)
    // Default to "0" if no value
    return value ? String(Number(value) / 100) : '0';
  };

  /**
   * Validate expense changelog is not empty
   */
  get expenseChangelogValidationErrors(): string[] {
    const schema = v.pipe(v.string(), v.minLength(1, 'Comment is required'));
    const result = v.safeParse(schema, this.expenseChangelog);
    return result.success ? [] : result.issues.map((issue) => issue.message);
  }

  /**
   * Check if expense form is valid (no validation errors and changelog is filled)
   */
  get isExpenseFormValid(): boolean {
    return this.expenseChangelogValidationErrors.length === 0;
  }

  toggleCourseDetailEdit = () => {
    const isCurrentlyEditing = this.editingCourseDetailsIndex === this.selectedCourseIndex;

    if (isCurrentlyEditing) {
      // Cancel - discard changes
      this.editingCourseDetailsIndex = null;
      this.courseDetailFieldsCopy = null;
      this.courseCustomFieldsCopy = null;
      this.detailChangelog = '';
    } else {
      // Enter edit mode
      const course = this.selectedCourse;
      if (!course) return;

      this.courseDetailFieldsCopy = trackedObject(course.fields);
      this.courseCustomFieldsCopy = course.customFields
        ? JSON.parse(JSON.stringify(course.customFields))
        : [];
      this.detailChangelog = '';
      this.editingCourseDetailsIndex = this.selectedCourseIndex;
    }
  };

  updateCourseDetailField = (fieldName: string, value: string | number) => {
    if (!this.courseDetailFieldsCopy) return;

    let valueToSet: string | number = value;

    if (fieldName === 'COURSE_CREDIT') {
      valueToSet = Math.round(Number(value));
    }

    setSingleValueForTasField(
      fieldName as keyof TasCourseModel['fields'],
      valueToSet,
      this.courseDetailFieldsCopy
    );
  };

  getCourseDetailValue = (fieldName: string): string => {
    if (!this.courseDetailFieldsCopy) return '';
    const value = getSingleValueForTasField(
      fieldName as keyof TasCourseModel['fields'],
      this.courseDetailFieldsCopy
    );
    return value ? String(value) : '';
  };

  updateDetailChangelog = (value: string) => {
    this.detailChangelog = value;
  };

  saveCourseDetails = dropTask(async () => {
    const course = this.selectedCourse;
    if (!course || !this.courseDetailFieldsCopy) {
      return;
    }

    // Validate changelog before saving
    if (!this.isDetailFormValid) {
      return;
    }

    try {
      // Update course with edited detail fields and custom fields
      course.fields = this.courseDetailFieldsCopy;
      if (this.courseCustomFieldsCopy) {
        course.customFields = this.courseCustomFieldsCopy;
      }

      // WarpDrive mutation (Phase 2.7) — replaces course.save({ adapterOptions: { changelog } }).
      await saveExistingRecordWithChangelog(this.store, course, this.detailChangelog);

      // No recalculation needed for detail fields (unlike expenses)
      this.tuitionAssistance.refreshActiveLimitsSummaryComponents();

      // Exit edit mode and clear tracked copies
      this.editingCourseDetailsIndex = null;
      this.courseDetailFieldsCopy = null;
      this.courseCustomFieldsCopy = null;
      this.detailChangelog = '';

      this.courseDetailsFor(course);
    } catch (error) {
      console.error('Error saving course details:', error);
      // TODO: Show error notification to user
    }
  });

  /**
   * Validate detail changelog is not empty
   */
  get detailChangelogValidationErrors(): string[] {
    const schema = v.pipe(v.string(), v.minLength(1, 'Comment is required'));
    const result = v.safeParse(schema, this.detailChangelog);
    return result.success ? [] : result.issues.map((issue) => issue.message);
  }

  /**
   * Check if detail form is valid (no validation errors and changelog is filled)
   * Validates both the changelog and all course custom fields
   */
  get isDetailFormValid(): boolean {
    // Check changelog validation
    if (this.detailChangelogValidationErrors.length > 0) {
      return false;
    }

    // Check all course custom fields for validation errors
    if (this.courseCustomFieldsCopy) {
      for (const field of this.courseCustomFieldsCopy) {
        const errors = this.getCourseCustomFieldValidationErrors(field.name || '');
        if (errors.length > 0) {
          return false;
        }
      }
    }

    return true;
  }

  /**
   * Get validation errors for a specific course custom field
   * Currently validates only required fields
   * @param fieldName - The name of the custom field
   * @returns Array of error messages for this field
   */
  getCourseCustomFieldValidationErrors = (fieldName: string): string[] => {
    if (!this.courseCustomFieldsCopy) return [];

    const field = this.courseCustomFieldsCopy.find((f) => f.name === fieldName);
    if (!field) return [];

    const errors: string[] = [];

    // Check required field validation
    if (field.required) {
      const hasValue =
        field.values &&
        field.values.length > 0 &&
        field.values[0] !== null &&
        field.values[0] !== undefined &&
        field.values[0] !== '';

      if (!hasValue) {
        errors.push(`${field.label || fieldName} is required`);
      }
    }

    // Future: Add more validation rules here (min/max length, patterns, etc.)

    return errors;
  };

  /**
   * Get form validation errors for course detail fields
   * Returns empty array for now as we don't have field-level validation yet
   */
  get courseDetailFormValidationErrors(): ValidationErrorSignature[] {
    return [];
  }

  /**
   * Check if the currently selected course details are being edited
   */
  get isEditingCourseDetails() {
    return this.editingCourseDetailsIndex === this.selectedCourseIndex;
  }

  /**
   * Get grade options for the selected course
   * Checks program template for field options or custom field inline options
   */
  get gradeOptions() {
    const course = this.selectedCourse;
    const programTemplate = this.args.model.tasProgramInstance?.tasProgramTemplate;

    if (!course || !programTemplate) return [];

    // Check if COURSE_GRADE is a standard field with tasFieldOptions
    const templateFields = programTemplate.fields as
      Record<string, { value_as_field_option?: boolean }> | undefined;
    const gradeConfig = templateFields?.['COURSE_GRADE'];

    if (gradeConfig?.value_as_field_option) {
      return getSortedOptionsByFieldName('COURSE_GRADE', programTemplate.tasFieldOptions);
    }

    const customField = course.customFields?.find((f) => f.name === 'COURSE_GRADE');
    if (customField) {
      // Check if custom field uses tasFieldOptions
      if (customField.value_as_field_option) {
        return getSortedOptionsByFieldName('COURSE_GRADE', programTemplate.tasFieldOptions);
      }
      return customField.options || [];
    }

    return [];
  }

  /**
   * Get list of editable detail field names
   */
  get editableCourseDetailFields() {
    return ['COURSE_NAME', 'COURSE_NUMBER', 'COURSE_DESCRIPTION', 'COURSE_CREDIT', 'COURSE_GRADE'];
  }

  /**
   * Handler for course custom field updates from drawer component
   * Updates the tracked copy during edit mode
   */
  didUpdateCourseCustomFields = (customFields: CustomFieldSignature[]) => {
    if (!this.courseCustomFieldsCopy) return;

    const updatedFields = this.updateFieldsWithNewValues(this.courseCustomFieldsCopy, customFields);

    this.courseCustomFieldsCopy = updatedFields;
  };

  /**
   * Get validation errors for course custom fields
   * Filters errors to only COURSE province fields
   */
  get courseCustomFieldValidationErrors(): ValidationErrorSignature[] {
    if (!this.courseCustomFieldsCopy) return [];

    return this.courseCustomFieldsCopy
      .filter((field) => field.province === 'COURSE')
      .map((field) => ({
        path: field.label || field.name || '',
        value: field.values?.[0]?.toString() || '',
        errors: this.getCourseCustomFieldValidationErrors(field.name || ''),
      }))
      .filter((error) => error.errors.length > 0);
  }

  /**
   * Get custom course fields (province: COURSE) for a specific course
   * Used in course details drawer
   * Returns tracked copy when editing, otherwise returns live model data
   */
  courseCustomFieldsFor = (course: TasCourseModel): CustomFieldSignature[] => {
    // When editing this specific course, use tracked copy
    if (
      this.isEditingCourseDetails &&
      course === this.selectedCourse &&
      this.courseCustomFieldsCopy
    ) {
      return this.courseCustomFieldsCopy
        .filter((field) => field.visible !== false)
        .sort((a, b) => (a.ordinal || 0) - (b.ordinal || 0));
    }

    // Otherwise use live model data
    if (!course?.customFields) return [];

    return course.customFields
      .filter((field) => field.visible !== false) // Show by default unless explicitly hidden
      .sort((a, b) => (a.ordinal || 0) - (b.ordinal || 0)) as CustomFieldSignature[];
  };

  /**
   * Get the expense type label from the application's EXPENSE_TYPE field
   * Uses the application model's expenseType getter, falls back to "Courses"
   */
  get expenseType(): string {
    const expenseType = this.args.model.tasProgramInstance?.tasProgramTemplate?.expenseType;
    return expenseType || 'Courses';
  }

  /**
   * Get the tab label for a course
   * Uses application's expenseType with index, falls back to "Course #"
   */
  getTabLabel = (index: number): string => {
    const appExpenseType = this.expenseType;
    return appExpenseType ? `${appExpenseType} ${index + 1}` : `Course ${index + 1}`;
  };

  /**
   * Get formatted expense items for a specific course
   * Uses field labels from program template for display
   */
  courseExpensesFor = (course: TasCourseModel) => {
    if (!course) return [];

    const programTemplate = this.args.model.tasProgramInstance?.tasProgramTemplate;

    // Helper to get field label from program template or use fallback
    const getFieldLabel = (fieldName: string, fallback: string): string => {
      // For standard fields, get label from program template
      const fields = course.fields as Record<string, { values: unknown[] }> | undefined;
      if (fields?.[fieldName] && programTemplate) {
        const templateFields = programTemplate.fields as ProgramTemplateFields;
        const config = templateFields?.[fieldName];
        if (config?.label) return config.label;
      }

      // For custom fields, label is on the field itself
      const customField = course.customFields?.find((f) => f.name === fieldName);
      if (customField?.label) return customField.label;

      return fallback;
    };

    const expenses = [
      {
        label: getFieldLabel('COURSE_TUITION', 'Tuition!'),
        amount: course.courseTuition ? `$${(course.courseTuition / 100).toFixed(2)}` : '$0.00',
        isTotal: false,
      },
      {
        label: getFieldLabel('COURSE_BOOKS', 'Books'),
        amount: course.courseBooks ? `$${(course.courseBooks / 100).toFixed(2)}` : '$0.00',
        isTotal: false,
      },
      {
        label: getFieldLabel('COURSE_LABS', 'Labs'),
        amount: course.courseLabs ? `$${(course.courseLabs / 100).toFixed(2)}` : '$0.00',
        isTotal: false,
      },
      {
        label: getFieldLabel('COURSE_SOFTWARE', 'Software'),
        amount: course.courseSoftware ? `$${(course.courseSoftware / 100).toFixed(2)}` : '$0.00',
        isTotal: false,
      },
      {
        label: getFieldLabel('COURSE_FEES', 'Fees'),
        amount: course.courseFees ? `$${(course.courseFees / 100).toFixed(2)}` : '$0.00',
        isTotal: false,
      },
      {
        label: 'Total',
        amount: course.courseExpenses ? `$${(course.courseExpenses / 100).toFixed(2)}` : '$0.00',
        isTotal: true,
      },
    ];

    return expenses;
  };

  /**
   * Get formatted detail rows for a specific course
   * Uses field labels from program template for display
   * Includes custom fields from customFields array
   */
  courseDetailsFor = (course: TasCourseModel) => {
    if (!course) return [];

    const programTemplate = this.args.model.tasProgramInstance?.tasProgramTemplate;

    // Helper to get field label from program template or use fallback
    const getFieldLabel = (fieldName: string, fallback: string): string => {
      // For standard fields, get label from program template
      const fields = course.fields as Record<string, { values: unknown[] }> | undefined;
      if (fields?.[fieldName] && programTemplate) {
        const templateFields = programTemplate.fields as ProgramTemplateFields;
        const config = templateFields?.[fieldName];
        if (config?.label) return config.label;
      }

      // For custom fields, label is on the field itself
      const customField = course.customFields?.find((f) => f.name === fieldName);
      if (customField?.label) return customField.label;

      return fallback;
    };

    const getFieldValue = (fieldName: string): string | number | boolean | null => {
      // Try standard fields
      const fields = course.fields as Record<string, { values: unknown[] }> | undefined;
      const value = fields?.[fieldName]?.values?.[0];
      if (value !== undefined) return value as string | number | boolean;

      // Try custom fields
      const customField = course.customFields?.find((f) => f.name === fieldName);
      return customField?.values?.[0] || null;
    };

    const details = [];

    // Course Name - show if has value
    const courseName = course.courseName || getFieldValue('COURSE_NAME');
    if (courseName) {
      details.push({
        label: getFieldLabel('COURSE_NAME', 'Course Name'),
        value: String(courseName),
      });
    }

    // Course Number - show if has value
    const courseNumber = course.courseNumber || getFieldValue('COURSE_NUMBER');
    if (courseNumber) {
      details.push({
        label: getFieldLabel('COURSE_NUMBER', 'Course Number'),
        value: String(courseNumber),
      });
    }

    // Course Description - show if has value
    const courseDescription = course.courseDescription || getFieldValue('COURSE_DESCRIPTION');
    if (courseDescription) {
      details.push({
        label: getFieldLabel('COURSE_DESCRIPTION', 'Course Description'),
        value: String(courseDescription),
      });
    }

    // Credits - show if has value
    const credits = course.courseCredit || getFieldValue('COURSE_CREDIT');
    if (credits) {
      details.push({
        label: getFieldLabel('COURSE_CREDIT', 'Credits'),
        value: String(credits),
      });
    }

    // Grade - show if has value and grades are enabled
    if (course.hasGrades) {
      const grade = course.courseGrade || getFieldValue('COURSE_GRADE');
      if (grade) {
        details.push({
          label: getFieldLabel('COURSE_GRADE', 'Grade'),
          value: String(grade),
        });
      }
    }

    // Filter for visible fields, sort by ordinal
    const customFields = (course.customFields || [])
      .filter((field) => field.visible !== false) // Show by default unless explicitly hidden
      .sort((a, b) => (a.ordinal || 0) - (b.ordinal || 0));

    /**
     * Format a single value based on field type
     */
    const formatCustomFieldValue = (
      value: string | boolean | number,
      field: CustomFieldSignature
    ): string => {
      // First check if there's an option mapping (for select/radio/checkbox fields)
      const option = field.options?.find(
        (opt: CustomFieldOption) =>
          opt.value === value || String(opt.value).toLowerCase() === String(value).toLowerCase()
      );
      if (option) {
        return option.label;
      }

      const inputType = field.inputType;

      if (inputType === 'currency') {
        // Value is in cents, convert to dollars
        const numValue = Number(value);
        if (isNaN(numValue)) return String(value);
        return `$${(numValue / 100).toFixed(2)}`;
      }

      if (inputType === 'date') {
        // Value is in format "YYYY-MM-DD", convert to "MM/DD/YYYY"
        const dateStr = String(value);
        try {
          const date = new Date(dateStr);
          if (!isNaN(date.getTime())) {
            return date.toLocaleDateString('en-US');
          }
        } catch {
          // If parsing fails, return as-is
        }
        return dateStr;
      }

      if (
        value === true ||
        value === false ||
        String(value).toLowerCase() === 'true' ||
        String(value).toLowerCase() === 'false'
      ) {
        return String(value).toLowerCase() === 'true' ? this.intl.t('yes') : this.intl.t('no');
      }

      // Default: return as string
      return String(value);
    };

    customFields.forEach((field) => {
      const values = field.values || [];
      if (values.length === 0) return;

      // Format value(s) based on inputType
      let formattedValue = '';
      if (values.length > 1) {
        formattedValue = values.map((value) => formatCustomFieldValue(value, field)).join(', ');
      } else if (values[0] !== undefined) {
        formattedValue = formatCustomFieldValue(values[0], field);
      }

      if (formattedValue) {
        details.push({
          label: field.label || field.name || '',
          value: formattedValue,
        });
      }
    });

    return details;
  };

  <template>
    {{pageTitle (t "tuition_assistance.application_approval.title")}}

    <VStack class="min-h-screen" ...attributes>
      <Section>
        <div class="flex-1 pb-32">
          <div class="max-w-7xl mx-auto">
            <VStack @collapsed={{true}}>
              {{! Application Header }}
              <Section>
                <:header>
                  {{yield to="header"}}
                </:header>
              </Section>

              {{#if this.isReadonlyView}}
                <div class="mt-4 mb-2 px-4">
                  <AlertBanner
                    @intent="info"
                    @live={{false}}
                    @title={{t "common.tas_approval_queue.read_only_title"}}
                    aria-label={{t "common.tas_approval_queue.read_only_title"}}
                    data-test-approval-read-only-banner
                  >
                    {{t "common.tas_approval_queue.read_only_body"}}
                  </AlertBanner>
                </div>
              {{/if}}

              {{! Approval Decision Assist Banner (admin only) — top of page }}
              {{#if this.features.isAdminApp}}
                <div class="mt-4 mb-2 px-4">
                  <ApprovalDecisionBanner
                    class="max-w-3xl"
                    @eligibilityData={{this.eligibilityData}}
                    @limitsData={{this.limitsData}}
                    @limitsUnavailable={{this.isLimitsUnavailable}}
                    @isLoading={{this.eligibilityLoading}}
                    @periodsOfEligibility={{this.periodsOfEligibilityData.value}}
                    @isPostPaymentReview={{this.isPostPaymentReview}}
                  />
                </div>
              {{/if}}

              {{! Admin-provided alerts (e.g., Course Cash verification banner) }}
              {{#if (has-block "alerts")}}
                {{yield to="alerts"}}
              {{/if}}

              <RecentCommentPreview @model={{@model}} @onViewMore={{this.openCommentsDrawer}} />

              <FinancialDetailsAdminSummary
                @application={{@model}}
                @limitsData={{this.limitsData}}
                @limitsUnavailable={{this.isLimitsUnavailable}}
              />

              {{! Repayment Warning - Displayed at top of page }}
              <RepaymentWarningBanner @model={{@model}} />

              {{! AI Analysis Section -- Temporarily disabled. Re-enabling needs imports:
                  Button ('tio-ui/components/button'), PhSparkle/PhThumbsUp/PhThumbsDown
                  ('ember-phosphor-icons') }}
              {{!-- {{#if this.hasAiRecommendation}}
                <Section>
                  <:body>
                    <div
                      class="tio-ai-analysis-surface"
                    >
                      {{! Header }}
                      <div
                        class="tio-row-between px-6 py-4 border-b tio-border-default"
                      >
                        <div class="tio-row-snug">
                          <PhSparkle @size="16" />
                          <span class="tio-text-base-semibold tio-fg-primary">AI
                            Analysis</span>
                        </div>
                        <div class="tio-row-tight">
                          <Button
                            @appearance="minimal"
                            @intent="default"
                            @size="sm"
                            class="tio-fg-muted hover:tio-fg-secondary"
                            title="Approve"
                            data-test-quick-approve-button
                          >
                            <PhThumbsUp @size="20" />
                          </Button>
                          <Button
                            @appearance="minimal"
                            @intent="default"
                            @size="sm"
                            class="tio-fg-muted hover:tio-fg-secondary"
                            title="Reject"
                            data-test-quick-reject-button
                          >
                            <PhThumbsDown @size="20" />
                          </Button>
                        </div>
                      </div>
                      {{! Body }}
                      <div class="px-6 py-4 space-y-3">
                        <div class="tio-flex-tight">
                          <span
                            class="tio-text-sm-semibold tio-fg-primary"
                          >Recommendation:</span>
                          <span
                            class="tio-text-sm-normal tio-fg-secondary"
                          >{{this.aiRecommendationStatus}}</span>
                        </div>
                        {{#if this.aiCalculationDetails}}
                          <p class="text-sm tio-fg-secondary leading-relaxed">
                            {{this.aiCalculationDetails}}
                          </p>
                        {{/if}}
                      </div>
                    </div>
                  </:body>
                </Section>
              {{/if}} --}}

              <ApplicationDetailsSection
                @model={{@model}}
                @isReadOnly={{this.isReadonlyView}}
                @isEditingApplicationDetails={{this.isEditingApplicationDetails}}
                @onEditingApplicationDetailsChange={{this.setIsEditingApplicationDetails}}
                @applicationFieldsCopy={{this.applicationFieldsCopy}}
                @onApplicationFieldsCopyChange={{this.setApplicationFieldsCopy}}
                @onApplicationTotalChange={{this.setEditableApplicationTotal}}
                @programTemplateRoute={{this.programTemplateRoute}}
                @updateFieldsWithNewValues={{this.updateFieldsWithNewValues}}
              />

              {{! Expenses Section }}
              <ExpensesSection
                @model={{@model}}
                @isReadOnly={{this.isReadonlyView}}
                @expenseType={{this.expenseType}}
                @onCourseOpen={{this.openCourseDrawer}}
                @onScholarshipOpen={{this.openScholarshipDrawer}}
              />
            </VStack>
          </div>
        </div>

        {{! New Footer with Action Buttons }}
        <ApprovalFooter
          @onDrawerOpen={{this.openDrawer}}
          @activeDrawer={{this.selectedDrawerType}}
          @approveButtonLoading={{false}}
          @application={{@model}}
          @isEditingApplication={{this.isEditingApplicationDetails}}
          @ai={{@ai}}
          @expenseType={{this.expenseType}}
          @onCourseCashOpen={{@onCourseCashOpen}}
        />
      </Section>
    </VStack>

    {{! Drawer Content }}
    <Drawer
      @isOpen={{this.isDrawerOpen}}
      @onClose={{this.closeDrawer}}
      @size="xl"
      @placement="right"
      @closeOnOutsideClick={{true}}
      @closeOnEscapeKey={{true}}
      @allowCloseButton={{true}}
      data-test-drawer
    >
      {{#if this.isAiAnalysis}}
        <AiAnalysisDrawer @application={{@model}} @recommendations={{@model.aiRecommendations}} />
      {{else if this.isComments}}
        <CommentsDrawer
          @model={{@model}}
          @authoredByPerson={{this.sessionContext.user.person}}
          @authoredByUser={{this.sessionContext.user}}
          @initialTab={{this.commentsInitialTab}}
        />
      {{else if this.isCourseDetails}}
        <CoursesDrawer
          @courses={{@model.activeCourses}}
          @selectedCourseIndex={{this.selectedCourseIndex}}
          @onCourseTabChange={{this.courseTabDidChange}}
          @courseExpensesFor={{this.courseExpensesFor}}
          @courseDetailsFor={{this.courseDetailsFor}}
          @courseCustomFieldsFor={{this.courseCustomFieldsFor}}
          @getTabLabel={{this.getTabLabel}}
          @programTemplate={{@model.tasProgramInstance.tasProgramTemplate}}
          @canEditCourse={{this.canEditCourse}}
          @isCourseTuitionLocked={{this.isCourseCashTuitionLocked}}
          {{! Expense editing props }}
          @isEditingExpenses={{this.isEditingCourseExpenses}}
          @onToggleExpenseEdit={{this.toggleCourseExpenseEdit}}
          @onSaveExpenses={{this.saveCourseExpenses.perform}}
          @onUpdateExpense={{this.updateCourseExpenseField}}
          @getCourseExpenseValue={{this.getCourseExpenseValue}}
          @expenseChangelog={{this.expenseChangelog}}
          @onExpenseChangelogUpdate={{this.updateExpenseChangelog}}
          @expenseChangelogErrors={{this.expenseChangelogValidationErrors}}
          @isExpenseFormValid={{this.isExpenseFormValid}}
          @isExpensesSaving={{this.saveCourseExpenses.isRunning}}
          {{! Detail editing props }}
          @isEditingDetails={{this.isEditingCourseDetails}}
          @onToggleDetailEdit={{this.toggleCourseDetailEdit}}
          @onSaveDetails={{this.saveCourseDetails.perform}}
          @onUpdateDetail={{this.updateCourseDetailField}}
          @onDidUpdateCourseCustomFields={{this.didUpdateCourseCustomFields}}
          @getCourseDetailValue={{this.getCourseDetailValue}}
          @courseCustomFieldValidationErrors={{this.courseCustomFieldValidationErrors}}
          @detailChangelog={{this.detailChangelog}}
          @onDetailChangelogUpdate={{this.updateDetailChangelog}}
          @detailChangelogErrors={{this.detailChangelogValidationErrors}}
          @isDetailFormValid={{this.isDetailFormValid}}
          @isDetailsSaving={{this.saveCourseDetails.isRunning}}
          @fieldsCopy={{this.courseDetailFieldsCopy}}
          @formValidationErrors={{this.courseDetailFormValidationErrors}}
        />
      {{else if this.isEmployerInfo}}
        <PartnerInfoDrawer
          @model={{@model}}
          @approverInstructionsItems={{this.approverInstructionsItems}}
          @employeeFaqsItems={{this.employeeFaqsItems}}
          @isAdminApp={{this.features.isAdminApp}}
        />
      {{else if this.isFinancialDetails}}
        <FinancialDetailsDrawer
          @application={{@model}}
          @programInstance={{@model.tasProgramInstance}}
          @editableApplicationTotal={{this.editableApplicationTotal}}
          @editableCourseCashTotal={{this.editableCourseCashTotal}}
          @isReadonlyView={{this.isReadonlyView}}
          @onUpdateApprovedTotal={{this.updateApprovedTotal}}
          @onSaveClaimsFinancials={{this.saveUpdatedClaimsFinancialsWithApprovedTotal}}
          @onUpdateClaimsFinancials={{this.didUpdateClaimsFinancials}}
          @onSetApplicationTotal={{this.setApplicationTotal}}
          @onSetCourseCashTotal={{this.setCourseCashTotal}}
          @onInvoiceDateChange={{this.updateInvoiceDate}}
        />
      {{else if this.isAttachments}}
        <AttachmentsDrawer
          @model={{@model}}
          @allAttachments={{this.allAttachments}}
          @classificationOptions={{classificationOptions}}
          @defaultClassification={{this.selectedClassification}}
          @programTemplate={{@model.tasProgramInstance.tasProgramTemplate}}
          @onUploadComplete={{this.hideUploadSection}}
          @hideDiscarded={{this.features.isEmployeeApp}}
          as |sections|
        >
          {{! Render upload section }}
          <sections.UploadSection
            @canUpload={{sections.uploadProps.canUpload}}
            @showUploadSection={{sections.uploadProps.showUploadSection}}
            @toggleUploadSection={{sections.uploadProps.toggleUploadSection}}
            @selectedClassification={{sections.uploadProps.selectedClassification}}
            @classificationDidChange={{sections.uploadProps.classificationDidChange}}
            @uploadFile={{sections.uploadProps.uploadFile}}
            @classificationOptions={{sections.uploadProps.classificationOptions}}
            @categoryOptions={{sections.uploadProps.categoryOptions}}
            @selectedCategory={{sections.uploadProps.selectedCategory}}
            @categoryDidChange={{sections.uploadProps.categoryDidChange}}
            @uploadDisabled={{sections.uploadProps.uploadDisabled}}
            @accept={{sections.uploadProps.accept}}
          />

          {{! Support backdoor: re-enqueue the application's PDF-generation jobs }}
          {{#if this.canGeneratePdfAsset}}
            <Button
              @intent="primary"
              @appearance="outlined"
              @icon={{PhFilePdf}}
              @label={{t
                "common.tas.approval.drawer.attachments.generate_pdf"
                default="Generate voucher PDF"
              }}
              @isRunning={{this.generatePdfAsset.isRunning}}
              class="w-full"
              {{on "click" this.openGeneratePdfConfirm}}
              data-test-generate-pdf-asset-button
            />

            <ConfirmationDialog
              @isOpen={{this.showGeneratePdfConfirm}}
              @title={{t
                "common.tas.approval.drawer.attachments.generate_pdf_confirm_title"
                default="Generate voucher PDF?"
              }}
              @intent={{if this.existingLifecycleDocumentsCount "warning" "info"}}
              @confirmText={{t
                "common.tas.approval.drawer.attachments.generate_pdf_confirm_action"
                default="Generate"
              }}
              @cancelText={{t "common.cancel" default="Cancel"}}
              @isRunning={{this.generatePdfAsset.isRunning}}
              @onConfirm={{this.generatePdfAsset.perform}}
              @onCancel={{this.closeGeneratePdfConfirm}}
              data-test-generate-pdf-confirm-dialog
            >
              <div class="tio-col-tight">
                {{#if this.existingLifecycleDocumentsCount}}
                  <AlertBanner @intent="warning" data-test-generate-pdf-existing-warning>
                    {{t
                      "common.tas.approval.drawer.attachments.generate_pdf_confirm_existing"
                      count=this.existingLifecycleDocumentsCount
                    }}
                  </AlertBanner>
                {{/if}}
                <p>
                  {{#if this.voucherDocumentName}}
                    {{t
                      "common.tas.approval.drawer.attachments.generate_pdf_confirm_body_named"
                      name=this.voucherDocumentName
                    }}
                  {{else}}
                    {{t
                      "common.tas.approval.drawer.attachments.generate_pdf_confirm_body"
                      default="This queues the voucher PDF for this application. It will appear with the other attachments once it finishes processing."
                    }}
                  {{/if}}
                </p>
              </div>
            </ConfirmationDialog>
          {{/if}}

          {{! Conditionally render analysis view for tio-admin, table view for others }}
          {{#if (eq this.consumingApp "tio-admin")}}
            <AttachmentsAnalysisView
              @attachments={{sections.attachments}}
              @assetSummaries={{sections.assetSummaries}}
              @deleteAttachment={{sections.deleteAttachment}}
              @isDeleted={{sections.isDeleted}}
              @isDevOps={{this.sessionContext.user.isTioDevOps}}
              @showOpenAll={{true}}
            />
          {{else}}
            <sections.TableView
              @sortedAttachments={{sections.tableProps.sortedAttachments}}
              @getFileIcon={{sections.tableProps.getFileIcon}}
              @getClassificationLabel={{sections.tableProps.getClassificationLabel}}
              @isDeleted={{sections.tableProps.isDeleted}}
              @deleteAttachment={{sections.tableProps.deleteAttachment}}
              @disabled={{sections.tableProps.disabled}}
              @showUploadSection={{sections.tableProps.showUploadSection}}
            />
          {{/if}}
        </AttachmentsDrawer>
      {{else if this.isTimeline}}
        <TimelineDrawer @history={{this.timelineHistory}} />
      {{else if this.isReassignApprover}}
        <ApprovalQueueStatusDrawer @application={{@model}} @consumingApp={{this.consumingApp}} />
      {{/if}}
    </Drawer>

    {{! Scholarship Drawer }}
    <AddScholarshipsDrawer
      @isOpen={{this.isScholarshipDrawerOpen}}
      @onClose={{this.closeScholarshipDrawer}}
      @application={{@model}}
    />
  </template>
}
